import { useState, useEffect } from 'react';

const STORAGE_KEY = 'advanced_making_progress';

export const useProgressTracking = () => {
  const [progress, setProgress] = useState({});
  const [completedCourses, setCompletedCourses] = useState([]);
  const [enrolledCourses, setEnrolledCourses] = useState([]);

  useEffect(() => {
    // Load progress from localStorage
    const savedProgress = localStorage.getItem(STORAGE_KEY);
    if (savedProgress) {
      const data = JSON.parse(savedProgress);
      setProgress(data.progress || {});
      setCompletedCourses(data.completedCourses || []);
      setEnrolledCourses(data.enrolledCourses || []);
    }
  }, []);

  const saveProgress = (newProgress, newCompleted, newEnrolled) => {
    const data = {
      progress: newProgress,
      completedCourses: newCompleted,
      enrolledCourses: newEnrolled,
      lastUpdated: new Date().toISOString()
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  };

  const updateCourseProgress = (courseId, weekNumber, completed = false) => {
    const newProgress = {
      ...progress,
      [courseId]: {
        ...progress[courseId],
        [`week_${weekNumber}`]: completed,
        lastActive: new Date().toISOString()
      }
    };

    const courseProgress = newProgress[courseId];
    const completedWeeks = Object.keys(courseProgress)
      .filter(key => key.startsWith('week_') && courseProgress[key])
      .length;
    
    courseProgress.completionPercentage = Math.round((completedWeeks / 12) * 100);
    courseProgress.weeksCompleted = completedWeeks;

    setProgress(newProgress);
    saveProgress(newProgress, completedCourses, enrolledCourses);
  };

  const enrollInCourse = (courseId) => {
    if (!enrolledCourses.includes(courseId)) {
      const newEnrolled = [...enrolledCourses, courseId];
      const newProgress = {
        ...progress,
        [courseId]: {
          ...progress[courseId],
          enrolledDate: new Date().toISOString(),
          completionPercentage: 0,
          weeksCompleted: 0,
          lastActive: new Date().toISOString()
        }
      };

      setEnrolledCourses(newEnrolled);
      setProgress(newProgress);
      saveProgress(newProgress, completedCourses, newEnrolled);
    }
  };

  const completeCourse = (courseId) => {
    if (!completedCourses.includes(courseId)) {
      const newCompleted = [...completedCourses, courseId];
      const newProgress = {
        ...progress,
        [courseId]: {
          ...progress[courseId],
          completedDate: new Date().toISOString(),
          completionPercentage: 100,
          weeksCompleted: 12
        }
      };

      setCompletedCourses(newCompleted);
      setProgress(newProgress);
      saveProgress(newProgress, newCompleted, enrolledCourses);
    }
  };

  const getCourseProgress = (courseId) => {
    return progress[courseId] || {
      completionPercentage: 0,
      weeksCompleted: 0,
      lastActive: null,
      enrolledDate: null,
      completedDate: null
    };
  };

  const getOverallStats = () => {
    const totalCourses = enrolledCourses.length + completedCourses.length;
    const totalProgress = Object.values(progress).reduce(
      (sum, course) => sum + (course.completionPercentage || 0),
      0
    );
    const averageProgress = totalCourses > 0 ? totalProgress / totalCourses : 0;

    return {
      totalEnrolled: enrolledCourses.length,
      totalCompleted: completedCourses.length,
      totalCourses,
      averageProgress: Math.round(averageProgress),
      activeCourses: enrolledCourses.filter(id => !completedCourses.includes(id)).length
    };
  };

  const resetProgress = () => {
    setProgress({});
    setCompletedCourses([]);
    setEnrolledCourses([]);
    localStorage.removeItem(STORAGE_KEY);
  };

  return {
    progress,
    completedCourses,
    enrolledCourses,
    updateCourseProgress,
    enrollInCourse,
    completeCourse,
    getCourseProgress,
    getOverallStats,
    resetProgress
  };
};